/**
 * CreditNotesPanel (Phase D1.1-C · Feb 2026)
 * ------------------------------------------
 * Global list of all Credit Notes (across invoices).
 * Non-editable; opening a row opens the parent invoice detail.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import InvoiceDetailModal from "@/components/InvoiceDetailModal";

const money = (v) =>
  `$${Number(v || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export default function CreditNotesPanel() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [openInvoiceId, setOpenInvoiceId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/commercial/credit-notes`, {
        withCredentials: true,
      });
      setItems(data);
    } catch {
      toast.error(t("cn_panel_load_err"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/commercial/settings`, {
          withCredentials: true,
        });
        setSettings(data);
      } catch {
        /* non-blocking */
      }
    })();
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const downloadPdf = async (cn) => {
    try {
      const res = await axios.get(`${API}/commercial/credit-notes/${cn.id}/pdf`, {
        responseType: "blob",
        withCredentials: true,
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch {
      toast.error(t("cn_panel_pdf_err"));
    }
  };

  return (
    <div data-testid="credit-notes-panel" className="space-y-6">
      <header>
        <div className="label-overline">DOC / CN</div>
        <h1 className="font-display text-3xl sm:text-5xl font-black tracking-tighter">
          {t("cn_panel_title")}
        </h1>
        <p className="text-base text-[var(--text-secondary)] mt-1">
          {t("cn_panel_subtitle", { n: items.length, s: items.length === 1 ? "" : "s" })}
        </p>
      </header>

      <section className="bg-white border border-[var(--border)] overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-[var(--bg)]">
            <tr>
              <Th>#</Th>
              <Th>{t("cn_panel_col_invoice")}</Th>
              <Th>{t("cn_panel_col_client")}</Th>
              <Th>{t("cn_panel_col_reason")}</Th>
              <Th>{t("cn_panel_col_method")}</Th>
              <Th>{t("cn_panel_col_date")}</Th>
              <Th className="text-right">{t("cn_panel_col_amount")}</Th>
              <Th />
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--border)]">
            {loading && (
              <tr>
                <td colSpan={8} className="p-6 text-center text-sm text-[var(--text-secondary)]">
                  {t("loading_ellipsis")}
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={8} className="p-6 text-center text-sm text-[var(--text-secondary)]">
                  {t("cn_panel_empty")}
                </td>
              </tr>
            )}
            {!loading &&
              items.map((cn) => (
                <tr
                  key={cn.id}
                  data-testid={`cn-row-${cn.number}`}
                  className="hover:bg-[var(--bg)] transition-colors"
                >
                  <Td>
                    <span className="font-mono-tech text-sm font-bold">{cn.number}</span>
                  </Td>
                  <Td>
                    <button
                      onClick={() => setOpenInvoiceId(cn.invoice_id)}
                      className="font-mono-tech text-xs underline decoration-dotted"
                    >
                      {cn.invoice_number}
                    </button>
                  </Td>
                  <Td>
                    <div className="text-sm font-bold">{cn.company}</div>
                  </Td>
                  <Td>
                    <div className="text-xs text-[var(--text-secondary)]">{cn.reason || "—"}</div>
                  </Td>
                  <Td>
                    <div className="text-xs">{cn.payment_method || "—"}</div>
                  </Td>
                  <Td>
                    <div className="text-xs">{String(cn.created_at || "").slice(0, 10)}</div>
                  </Td>
                  <Td className="text-right font-mono-tech text-sm font-bold text-rose-700">
                    -{money(cn.amount)}
                  </Td>
                  <Td>
                    <button
                      onClick={() => downloadPdf(cn)}
                      data-testid={`cn-pdf-${cn.number}`}
                      className="font-mono-tech text-[0.65rem] font-bold border border-[var(--border)] px-2 py-1 hover:bg-black hover:text-white transition-colors"
                    >
                      PDF
                    </button>
                  </Td>
                </tr>
              ))}
          </tbody>
        </table>
      </section>

      {openInvoiceId && (
        <InvoiceDetailModal
          invoiceId={openInvoiceId}
          onClose={() => setOpenInvoiceId(null)}
          onChanged={load}
          settings={settings}
        />
      )}
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`px-3 py-2 text-left font-mono-tech text-[0.7rem] font-bold tracking-[0.2em] text-[var(--text-secondary)] ${className}`}
    >
      {children}
    </th>
  );
}
function Td({ children, className = "" }) {
  return <td className={`px-3 py-2 ${className}`}>{children}</td>;
}
